import { useState, useEffect, useCallback } from 'react';
import { MarketData } from 'common/src/utils/types';
import {
  getMarketDataBySelectedDate,
  getMarketDataByDateRange,
} from 'common/src/api/market_data';
import { toast } from 'react-toastify';
import {
  loadMarketDataCommissions,
  MarketDataCommissions,
} from 'common/src/api/presettlement-settings';
import { RMB_TO_USD } from '../../../common/src/utils/definitions';

import useDataStore, {
  PriceLevel,
} from '../../../common/src/stores/useDataStore';
import useUserStore from '../../../common/src/stores/useUserStore';
import { useTranslation } from './useTranslation';

interface UseMarketDataProps {
  viewMode?: 'day' | 'week';
  selectedDate?: string;
  dateRange?: [Date, Date];
  enabled?: boolean;
  reloadTrigger?: number; // Add trigger to force reload
  forcePriceLevel?: PriceLevel; // Force a specific price level (overrides global store)
  forceSpeciesId?: number; // Force a specific species id (e.g., 2 for cherries in importers app)
}

/**
 * Calculate price based on level (FOB or Producer)
 * All prices are calculated in USD per Kg
 */
const calculatePriceByLevel = (
  item: MarketData,
  level: PriceLevel,
  settings: MarketDataCommissions
): number => {
  // Convert to USD/Kg if needed
  let priceUsdPerKg = RMB_TO_USD(item.price_kg);

  if (level === 'market') {
    return priceUsdPerKg;
  }

  // Determine freight costs based on transport type
  const isAir = item.transport === 'AIR';
  const freightCost = isAir
    ? settings.air_freight_cost
    : settings.ocean_freight_cost;
  const otherCosts = isAir
    ? settings.air_other_costs
    : settings.ocean_other_costs;

  // Calculate FOB: Price - Receiver Commission - VAT - Freight - Other Costs
  const receiverCommission =
    priceUsdPerKg * (settings.receiver_commission / 100);
  const vat = priceUsdPerKg * (settings.vat_tax / 100);
  const fobPrice =
    priceUsdPerKg - receiverCommission - vat - freightCost - otherCosts;

  if (level === 'fob') {
    return Math.max(0, fobPrice); // Ensure non-negative
  }

  // Calculate Producer Return: FOB - Exporter Commission - Packing Cost
  const exporterCommission = fobPrice * (settings.exporter_commission / 100);
  const producerReturn = fobPrice - exporterCommission - settings.packing_cost;

  return Math.max(0, producerReturn); // Ensure non-negative
};

const useMarketData = ({
  viewMode,
  selectedDate,
  dateRange,
  enabled = true,
  reloadTrigger,
  forcePriceLevel,
  forceSpeciesId,
}: UseMarketDataProps) => {
  const { t } = useTranslation('common');
  const { session } = useUserStore();
  const { selectedSpecies, priceLevel: globalPriceLevel } = useDataStore();
  const { userProfile } = useUserStore();

  // Use forcePriceLevel if provided, otherwise use global priceLevel
  const priceLevel = forcePriceLevel ?? globalPriceLevel;
  // Use forceSpeciesId if provided, otherwise use selectedSpecies from store
  const speciesId = forceSpeciesId ?? selectedSpecies?.id;
  const [marketData, setMarketData] = useState<MarketData[]>([]);
  const [loading, setLoading] = useState(true);
  const [commissions, setCommissions] = useState<MarketDataCommissions | null>(
    null
  );
  const [needsSettings, setNeedsSettings] = useState(false);

  // Load commissions when species or priceLevel changes
  useEffect(() => {
    const loadCommissions = async () => {
      if (
        priceLevel === 'market' ||
        !speciesId ||
        !userProfile?.exporter?.id ||
        !session
      ) {
        setCommissions(null);
        setNeedsSettings(false);
        return;
      }

      const settings = await loadMarketDataCommissions(
        userProfile.exporter.id,
        speciesId,
        session
      );

      if (settings) {
        setCommissions(settings);
        setNeedsSettings(false);
      } else {
        setCommissions(null);
        setNeedsSettings(true);
      }
    };

    loadCommissions();
  }, [
    priceLevel,
    speciesId,
    userProfile?.exporter?.id,
    reloadTrigger, // Add this to force reload when trigger changes
  ]);

  // Apply price calculations
  const applyPriceCalculations = useCallback(
    (data: MarketData[]): MarketData[] => {
      if (priceLevel === 'market' || !commissions) {
        return data;
      }

      return data.map((item) => ({
        ...item,
        price_kg: calculatePriceByLevel(item, priceLevel, commissions),
      }));
    },
    [priceLevel, commissions]
  );

  // Keep raw data separate from processed data
  const [rawMarketData, setRawMarketData] = useState<MarketData[]>([]);

  useEffect(() => {
    const loadData = async () => {
      if (!enabled) return;

      // Check if we need settings before loading data
      if (priceLevel !== 'market' && needsSettings) {
        setMarketData([]);
        setRawMarketData([]);
        setLoading(false);
        return;
      }

      // const toastId = toast.loading(t('Cargando datos del mercado...'));
      setLoading(true);
      try {
        let data: MarketData[] = [];

        if (viewMode === 'day' && selectedDate) {
          data = await getMarketDataBySelectedDate(selectedDate, speciesId);
        } else if (dateRange) {
          data = await getMarketDataByDateRange(
            dateRange[0].toISOString(),
            dateRange[1].toISOString(),
            speciesId
          );
        }
        console.log(`Se obtuvieron ${data.length} registros`);

        // Store raw data
        setRawMarketData(data);

        // Apply price calculations based on level
        const processedData = applyPriceCalculations(data);
        setMarketData(processedData);

        // toast.update(toastId, {
        //   render: t('Datos cargados exitosamente'),
        //   type: 'success',
        //   isLoading: false,
        //   autoClose: 2000,
        // });
      } catch (error) {
        console.error('Error fetching market data:', error);
        setMarketData([]);
        setRawMarketData([]);
        // toast.update(toastId, {
        //   render: t('Error al cargar los datos'),
        //   type: 'error',
        //   isLoading: false,
        //   autoClose: 3000,
        // });
      } finally {
        setLoading(false);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    viewMode,
    selectedDate,
    dateRange?.[0]?.getTime(),
    dateRange?.[1]?.getTime(),
    enabled,
    speciesId,
    // Removed priceLevel from dependencies - we don't want to refetch when it changes
    needsSettings,
  ]);

  // Recalculate prices when priceLevel or commissions change (without refetching)
  useEffect(() => {
    if (rawMarketData.length > 0) {
      const processedData = applyPriceCalculations(rawMarketData);
      setMarketData(processedData);
    }
  }, [priceLevel, commissions, applyPriceCalculations, rawMarketData]);

  return { marketData, loading, needsSettings, commissions };
};
export default useMarketData;
